import { Subject, BehaviorSubject, Observable, from, of, timer } from 'rxjs';
import { 
    switchMap, 
    catchError, 
    tap, 
    retryWhen, 
    delayWhen, 
    take,
    filter,
    mergeMap,
    finalize
} from 'rxjs/operators';
import { CoordinatorService } from './coordinator.service';
import { AttachmentStorage, AttachmentRecord, AttachmentSyncStatus } from './attachment-storage';

/**
 * Configuration for sync retry behavior
 */
interface SyncRetryConfig {
    maxRetries: number;
    initialDelayMs: number;
    maxDelayMs: number;
    backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: SyncRetryConfig = {
    maxRetries: 5,
    initialDelayMs: 1000,
    maxDelayMs: 32000,
    backoffMultiplier: 2,
};

/**
 * Upload task for tracking in-progress uploads
 */
interface UploadTask {
    record: AttachmentRecord;
    retryCount: number;
    startedAt: number;
}

/**
 * AttachmentSyncManager - Manages cloud synchronization for attachments
 * 
 * Features:
 * - Immediate upload with exponential backoff retry
 * - Download on demand (lazy loading from cloud)
 * - Tracks upload progress and failures
 * - Uses RxJS for reactive sync operations
 */
export class AttachmentSyncManager {
    private _storage: AttachmentStorage;
    private _retryConfig: SyncRetryConfig;
    
    // Track active uploads
    private _activeUploads = new Map<string, UploadTask>();
    
    // Subjects for reactive updates
    private _uploadProgress$ = new Subject<{ id: string; projectId: string; progress: 'started' | 'success' | 'failed' | 'retrying' }>();
    private _uploadQueue$ = new Subject<AttachmentRecord>();
    private _isUploading$ = new BehaviorSubject<boolean>(false);

    constructor(
        private coords: CoordinatorService,
        storage: AttachmentStorage,
        retryConfig: Partial<SyncRetryConfig> = {}
    ) {
        this._storage = storage;
        this._retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
        
        this._initUploadProcessor();
    }

    /**
     * Observable for upload progress events
     */
    get uploadProgress$(): Observable<{ id: string; projectId: string; progress: 'started' | 'success' | 'failed' | 'retrying' }> {
        return this._uploadProgress$.asObservable();
    }

    /**
     * Observable indicating if any uploads are in progress
     */
    get isUploading$(): Observable<boolean> {
        return this._isUploading$.asObservable();
    }

    /**
     * Check if there are active uploads
     */
    get hasActiveUploads(): boolean {
        return this._activeUploads.size > 0;
    }

    /**
     * Initialize the upload queue processor
     */
    private _initUploadProcessor(): void {
        this._uploadQueue$.pipe(
            // Process uploads concurrently (max 3 at a time)
            mergeMap(record => this._processUpload(record), 3)
        ).subscribe();
    }

    /**
     * Queue an attachment for upload to cloud
     */
    async queueUpload(id: string, projectId: string): Promise<void> {
        const record = await this._storage.get(id, projectId);
        if (!record) {
            console.error('Cannot queue upload: attachment not found', { id, projectId });
            return;
        }

        if (record.syncStatus === 'synced') {
            console.log('Attachment already synced', { id });
            return;
        }

        // Don't queue if already uploading
        if (this._activeUploads.has(id)) {
            console.log('Attachment already in upload queue', { id });
            return;
        }

        this._uploadQueue$.next(record);
    }

    /**
     * Queue all pending attachments for a project
     */
    async queueProjectUploads(projectId: string): Promise<number> {
        const pending = await this._storage.getByProjectAndStatus(projectId, 'pending');
        const failed = await this._storage.getByProjectAndStatus(projectId, 'failed');
        
        const toUpload = [...pending, ...failed];
        
        for (const record of toUpload) {
            this._uploadQueue$.next(record);
        }
        
        return toUpload.length;
    }

    /**
     * Process a single upload with exponential backoff retry
     */
    private _processUpload(record: AttachmentRecord): Observable<boolean> {
        const { id, projectId } = record;
        
        // Track this upload
        this._activeUploads.set(id, {
            record,
            retryCount: 0,
            startedAt: Date.now()
        });
        
        this._updateUploadingState();
        this._uploadProgress$.next({ id, projectId, progress: 'started' });

        return from(this._performUpload(record)).pipe(
            retryWhen(errors => 
                errors.pipe(
                    delayWhen((error, retryIndex) => {
                        const task = this._activeUploads.get(id);
                        if (task) {
                            task.retryCount = retryIndex + 1;
                        }

                        if (retryIndex >= this._retryConfig.maxRetries) {
                            throw error; // Exceeded max retries
                        }

                        // Calculate exponential backoff delay
                        const delay = Math.min(
                            this._retryConfig.initialDelayMs * Math.pow(this._retryConfig.backoffMultiplier, retryIndex),
                            this._retryConfig.maxDelayMs
                        );

                        console.log(`Retrying upload in ${delay}ms (attempt ${retryIndex + 1}/${this._retryConfig.maxRetries})`, { id });
                        this._uploadProgress$.next({ id, projectId, progress: 'retrying' });

                        // Update status to pending for retry
                        this._storage.updateSyncStatus(id, projectId, 'pending');

                        return timer(delay);
                    }),
                    take(this._retryConfig.maxRetries)
                )
            ),
            tap(() => {
                // Success
                console.log('%c Attachment uploaded successfully', 'color: green;', { id, projectId });
                this._uploadProgress$.next({ id, projectId, progress: 'success' });
            }),
            catchError((error) => {
                // Failed after all retries
                console.error('Attachment upload failed after retries', { id, projectId, error });
                // Update status asynchronously (fire and forget)
                this._storage.updateSyncStatus(id, projectId, 'failed');
                this._uploadProgress$.next({ id, projectId, progress: 'failed' });
                return of(false);
            }),
            finalize(() => {
                this._activeUploads.delete(id);
                this._updateUploadingState();
            })
        );
    }

    /**
     * Perform the actual upload to Firebase Storage
     */
    private async _performUpload(record: AttachmentRecord): Promise<boolean> {
        const { id, projectId, blob, mimetype } = record;

        // Update status to uploading
        await this._storage.updateSyncStatus(id, projectId, 'uploading');

        // Get the remote path
        const cloudPath = this._getCloudPath(id, projectId);

        // Upload to Firebase Storage
        const success = await this.coords.firebase$.storage.uploadBlob(
            cloudPath,
            new Blob([blob], { type: mimetype })
        );

        if (!success) {
            throw new Error('Upload to Firebase Storage failed');
        }

        // Update status to synced with cloud path
        await this._storage.updateSyncStatus(id, projectId, 'synced', cloudPath);

        return true;
    }

    /**
     * Download an attachment from cloud storage (on demand)
     */
    async downloadFromCloud(
        id: string, 
        projectId: string,
        filename: string,
        mimetype: string
    ): Promise<AttachmentRecord | null> {
        // Check if already exists locally
        const existing = await this._storage.get(id, projectId);
        if (existing) {
            return existing;
        }

        const cloudPath = this._getCloudPath(id, projectId);

        try {
            console.log('Downloading attachment from cloud', { id, cloudPath });
            
            const blob = await this.coords.firebase$.storage.downloadFile2(cloudPath);
            
            if (!blob) {
                console.error('Failed to download attachment from cloud', { id, cloudPath });
                return null;
            }

            // Store in IndexedDB as cached from cloud
            const result = await this._storage.storeFromCloud(
                blob,
                id,
                projectId,
                filename,
                mimetype,
                cloudPath
            );

            if (result.success && result.record) {
                console.log('%c Attachment downloaded and cached', 'color: blue;', { id, projectId });
                return result.record;
            }

            return null;
        } catch (e) {
            console.error('Error downloading attachment from cloud', { id, cloudPath, error: e });
            return null;
        }
    }

    /**
     * Get the cloud storage path for an attachment
     */
    private _getCloudPath(id: string, projectId: string): string {
        const userId = this.coords.project$.current?.team?.owner.id || this.coords.user$.currentId;
        return `userdata/${userId}/projects/${projectId}/Attachments/${id}`;
    }

    /**
     * Update the isUploading state
     */
    private _updateUploadingState(): void {
        this._isUploading$.next(this._activeUploads.size > 0);
    }

    /**
     * Get upload status for an attachment
     */
    getUploadStatus(id: string): { isUploading: boolean; retryCount: number } | null {
        const task = this._activeUploads.get(id);
        if (!task) return null;
        
        return {
            isUploading: true,
            retryCount: task.retryCount
        };
    }

    /**
     * Cancel all pending uploads for a project
     */
    async cancelProjectUploads(projectId: string): Promise<void> {
        // Mark all pending/failed as synced to stop retries
        // (This is a soft cancel - active uploads will complete)
        const pending = await this._storage.getByProjectAndStatus(projectId, 'pending');
        
        for (const record of pending) {
            this._activeUploads.delete(record.id);
        }
        
        this._updateUploadingState();
    }

    /**
     * Retry all failed uploads for a project
     */
    async retryFailedUploads(projectId: string): Promise<number> {
        const failed = await this._storage.getByProjectAndStatus(projectId, 'failed');
        
        for (const record of failed) {
            // Reset failure count and queue for upload
            record.syncFailureCount = 0;
            record.syncStatus = 'pending';
            await this._storage.updateSyncStatus(record.id, projectId, 'pending');
            this._uploadQueue$.next(record);
        }
        
        return failed.length;
    }

    /**
     * Check if an attachment needs to be downloaded from cloud
     */
    async needsDownload(id: string, projectId: string): Promise<boolean> {
        const record = await this._storage.get(id, projectId);
        return !record;
    }

    /**
     * Get sync statistics for a project
     */
    async getProjectSyncStats(projectId: string): Promise<{
        total: number;
        synced: number;
        pending: number;
        uploading: number;
        failed: number;
    }> {
        const counts = await this._storage.getStatusCounts(projectId);
        
        return {
            total: Array.from(counts.values()).reduce((a, b) => a + b, 0),
            synced: counts.get('synced') || 0,
            pending: counts.get('pending') || 0,
            uploading: counts.get('uploading') || 0,
            failed: counts.get('failed') || 0,
        };
    }
}

